import type { UniqueIdentifier } from '@risksmart-app/atomic-ui';
import {
  Button,
  cn,
  Draggable,
  FieldError,
  FieldLabel,
  Icon,
  Input,
} from '@risksmart-app/atomic-ui';
import { useCallback, useRef } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import type { FieldEditorValues } from '../config';

export const OptionsEditor = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FieldEditorValues>();
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'options',
  });
  const optionsError =
    errors.options && !Array.isArray(errors.options)
      ? (errors.options as { message?: string }).message
      : undefined;

  const itemIds = fields.map((f) => f.id);
  const activeIdRef = useRef<UniqueIdentifier | null>(null);
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;

  const handleReorder = useCallback(
    (reordered: UniqueIdentifier[]) => {
      const activeId = activeIdRef.current;
      if (!activeId) {
        return;
      }
      const fromIndex = fieldsRef.current.findIndex((f) => f.id === activeId);
      const toIndex = reordered.indexOf(activeId);
      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        move(fromIndex, toIndex);
      }
      activeIdRef.current = null;
    },
    [move]
  );

  return (
    <div className={cn('flex flex-col gap-3')}>
      <FieldLabel>{'Options'}</FieldLabel>
      <Draggable
        items={itemIds}
        onReorder={handleReorder}
        onDragStart={(event) => {
          activeIdRef.current = event.active.id;
        }}
        className={cn('flex flex-col gap-2')}
      >
        {fields.map((item, index) => (
          <Draggable.Item key={item.id} id={item.id}>
            <Controller
              control={control}
              name={`options.${index}.label`}
              render={({ field, fieldState }) => (
                <div className={cn('flex items-center gap-2')}>
                  <Draggable.DragHandle
                    className={cn('text-neutral-active shrink-0')}
                  />
                  <div className={cn('flex-1')}>
                    <Input
                      placeholder={`Option ${index + 1}`}
                      aria-invalid={!!fieldState.error}
                      {...field}
                    />
                    {fieldState.error ? (
                      <FieldError>{fieldState.error.message}</FieldError>
                    ) : null}
                  </div>
                  <Button
                    variant={'destructive'}
                    style={'ghost'}
                    size={'icon'}
                    className={cn('p-0 size-auto shrink-0')}
                    onClick={() => remove(index)}
                  >
                    <Icon name={'trash-2'} size={'sm'} />
                  </Button>
                </div>
              )}
            />
          </Draggable.Item>
        ))}
        <Draggable.Overlay>
          {(activeId) => {
            const activeIndex = fields.findIndex((f) => f.id === activeId);
            const activeField = fields[activeIndex];
            if (!activeField) {
              return null;
            }

            return (
              <div className={cn('flex items-center gap-2')}>
                <Draggable.DragHandle className={cn('text-neutral-active')} />
                <div className={cn('flex-1')}>
                  <Input
                    placeholder={`Option ${activeIndex + 1}`}
                    value={activeField.label}
                    readOnly
                  />
                </div>
                <Button
                  variant={'destructive'}
                  style={'ghost'}
                  size={'icon'}
                  className={cn('p-0 size-auto shrink-0')}
                  disabled
                >
                  <Icon name={'trash-2'} size={'sm'} />
                </Button>
              </div>
            );
          }}
        </Draggable.Overlay>
      </Draggable>
      {optionsError ? <FieldError>{optionsError}</FieldError> : null}
      <Button
        style={'dashed-fill'}
        radius={'xl'}
        className={'w-full'}
        onClick={() => append({ id: crypto.randomUUID(), label: '' })}
      >
        <Icon name={'plus'} size={'sm'} />
        {'Add option'}
      </Button>
    </div>
  );
};
